import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Activity as ActivityIcon,
  Bell,
  CheckCheck,
  Loader2,
  LogIn,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { selectAppUser } from "../redux/appAuthSlice";
import {
  fetchUserActivities,
  fetchActivitySummary,
  markActivityRead,
  markAllActivitiesRead,
  deleteActivityById,
  clearActivities,
} from "../redux/activitySlice";
import {
  ACTIVITY_FILTERS,
  accentForAction,
  buildTimelineSections,
  formatActionLabel,
  formatAgo,
  getActivityDateKey,
  matchesActivityFilter,
} from "../utils/activityHelpers";
import { THEME } from "../theme";
import FollowingP2PActivityCard from "../components/activity/FollowingP2PActivityCard";
import FollowingBetHouseLiveActivityCard from "../components/activity/FollowingBetHouseLiveActivityCard";
import FollowingPredictionActivityCard from "../components/activity/FollowingPredictionActivityCard";
import {
  isFollowingBetHouseLiveActivity,
  isFollowingP2PActivity,
  isFollowingPredictionActivity,
  navigateFromActivity,
} from "../utils/activityNavigation";
import AppLiveClock from "../components/ui/AppLiveClock";
import { getAccountInitial } from "../components/ui/AccountProfileMenu";
import generateAvatarUrl from "../utils/generateAvatarUrl";
import "../activity.css";

const PER_SECTION = 8;

const ActivityProfileAvatar = ({ user }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const label = user?.name || user?.email || "Your profile";

  return (
    <Link
      to="/app/profile"
      className="act-toolbar-avatar-link"
      aria-label={`${label} profile`}
    >
      {imageFailed ? (
        <span className="act-toolbar-avatar act-toolbar-avatar-initials" aria-hidden>
          {getAccountInitial(user)}
        </span>
      ) : (
        <img
          className="act-toolbar-avatar"
          src={generateAvatarUrl(user?.name || user?.email)}
          alt=""
          width={28}
          height={28}
          onError={() => setImageFailed(true)}
        />
      )}
    </Link>
  );
};

const ActivityHero = ({ unreadCount, total }) => (
  <div className="act-hero" aria-label="Pulse summary">
    <div className="act-hero-stat">
      <span>Unread</span>
      <strong className="tabular-nums">{unreadCount}</strong>
    </div>
    <div className="act-hero-stat">
      <span>Events</span>
      <strong className="tabular-nums">{total}</strong>
    </div>
    <p className="act-hero-blurb">Bets, wallet, invites, and rewards in one feed.</p>
  </div>
);

const PreviewStrip = ({ preview }) => {
  if (!preview?.length) return null;
  return (
    <div className="act-preview">
      <p className="act-preview-label">
        <Bell size={12} />
        Latest unread
      </p>
      <div className="act-preview-track">
        {preview.map((item, i) => {
          const { color } = accentForAction(item.action);
          return (
            <div key={`${item.createdAt}-${i}`} className="act-preview-card">
              <span className="act-preview-dot" style={{ backgroundColor: color }} />
              <span className="act-preview-title">{item.title || "Update"}</span>
              <span className="act-preview-time">{formatAgo(item.createdAt)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ActivityRow = ({ item, onRead, onDelete }) => {
  const { color, Icon, label } = accentForAction(item.action);
  const unread = !item.isRead;

  return (
    <article
      className={`act-row ${unread ? "act-row-unread" : ""}`}
      onClick={() => unread && onRead(item._id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && unread) onRead(item._id);
      }}
      role="button"
      tabIndex={0}
    >
      <div className="act-row-spine" style={{ backgroundColor: color }} aria-hidden />
      <div className="act-row-icon" style={{ borderColor: color, color }}>
        <Icon size={17} strokeWidth={2.2} />
      </div>
      <div className="act-row-body">
        <div className="act-row-head">
          <h3 className="act-row-title">{item.title || "Update"}</h3>
          {unread && <span className="act-row-dot" aria-label="Unread" />}
        </div>
        {item.message && <p className="act-row-message">{item.message}</p>}
        <div className="act-row-meta">
          <span>{formatAgo(item.createdAt)}</span>
          <span className="act-row-pill" style={{ color, borderColor: `${color}44` }}>
            {label}
          </span>
          <span className="act-row-action">{formatActionLabel(item.action)}</span>
        </div>
      </div>
      <button
        type="button"
        className="act-row-delete"
        aria-label="Remove activity"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item._id);
        }}
      >
        <Trash2 size={14} />
      </button>
    </article>
  );
};

const Activity = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectAppUser);
  const {
    activities,
    loading,
    pagination,
    error,
    unreadCount,
    summaryPreview,
    markAllLoading,
  } = useSelector((s) => s.activities);

  const [filter, setFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [sectionWindows, setSectionWindows] = useState({});
  const loadMoreRef = useRef(null);

  const loadPage = useCallback(
    (page = 1) => {
      if (!user?._id) return Promise.resolve();
      const isRead = filter === "unread" ? false : null;
      return dispatch(
        fetchUserActivities({ userId: user._id, page, limit: 25, isRead })
      );
    },
    [dispatch, user?._id, filter]
  );

  useEffect(() => {
    if (!user?._id) return undefined;
    dispatch(fetchActivitySummary());
    return () => dispatch(clearActivities());
  }, [user?._id, dispatch]);

  useEffect(() => {
    if (!user?._id) return;
    setSectionWindows({});
    const isRead = filter === "unread" ? false : null;
    dispatch(fetchUserActivities({ userId: user._id, page: 1, limit: 25, isRead }));
  }, [filter, user?._id, dispatch]);

  const filtered = useMemo(() => {
    const sorted = [...(activities || [])].sort(
      (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0)
    );
    if (filter === "all" || filter === "unread") return sorted;
    return sorted.filter((item) => matchesActivityFilter(item, filter));
  }, [activities, filter]);

  const grouped = useMemo(
    () => buildTimelineSections(filtered, getActivityDateKey),
    [filtered]
  );

  const displaySections = useMemo(
    () =>
      grouped.map((section) => {
        const total = section.data.length;
        const pageCount = Math.max(1, Math.ceil(total / PER_SECTION));
        const rawIndex = sectionWindows[section.dateKey] ?? 0;
        const windowIndex = Math.min(rawIndex, pageCount - 1);
        const start = windowIndex * PER_SECTION;
        return {
          ...section,
          visible: section.data.slice(start, start + PER_SECTION),
          showNewer: windowIndex > 0,
          showOlder: windowIndex < pageCount - 1,
          rangeLabel:
            total > PER_SECTION
              ? `${start + 1}–${Math.min(start + PER_SECTION, total)} of ${total}`
              : null,
          windowIndex,
        };
      }),
    [grouped, sectionWindows]
  );

  const refresh = async () => {
    if (!user?._id) return;
    setRefreshing(true);
    setSectionWindows({});
    try {
      await Promise.all([dispatch(fetchActivitySummary()), loadPage(1)]);
    } finally {
      setRefreshing(false);
    }
  };

  const handleMarkAll = async () => {
    if (unreadCount === 0 || markAllLoading) return;
    try {
      await dispatch(markAllActivitiesRead()).unwrap();
      await Promise.all([dispatch(fetchActivitySummary()), loadPage(1)]);
    } catch {
      /* slice keeps prior state on failure */
    }
  };

  const handleDelete = (id) => {
    if (!window.confirm("Remove this line from your timeline?")) return;
    dispatch(deleteActivityById(id));
  };

  const markActivitySeen = useCallback(
    (id) => {
      dispatch(markActivityRead(id));
      dispatch(fetchActivitySummary());
    },
    [dispatch]
  );

  const handleViewOffer = useCallback(
    (item) => {
      if (!item?.isRead) {
        dispatch(markActivityRead(item._id));
        dispatch(fetchActivitySummary());
      }
      navigateFromActivity(navigate, item);
    },
    [dispatch, navigate]
  );

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !pagination.hasNext || loading) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && pagination.hasNext && !loading) {
          loadPage(pagination.currentPage + 1);
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [pagination, loading, loadPage]);

  if (!user?._id) {
    return (
      <div className="act-page">
        <header className="act-toolbar">
          <div className="act-toolbar-cluster act-toolbar-cluster-start">
            <h1 className="act-toolbar-title">Bond Pulse</h1>
          </div>
          <div className="act-toolbar-center app-chrome-clock-center">
            <AppLiveClock className="app-chrome-clock" />
          </div>
          <div className="act-toolbar-actions" />
        </header>
        <div className="act-gate">
          <ActivityIcon size={32} style={{ color: THEME.primarySoft }} />
          <h2 className="act-gate-title">Sign in to see your story</h2>
          <p className="act-gate-sub">Track bets, wallet moves, and account updates.</p>
          <Link to="/app/login" state={{ from: "/app/activity" }} className="act-gate-btn">
            <LogIn size={16} />
            Log in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="act-page">
      <header className="act-toolbar">
        <div className="act-toolbar-cluster act-toolbar-cluster-start">
          <div className="act-toolbar-identity">
            <ActivityProfileAvatar user={user} />
            <h1 className="act-toolbar-title">Bond Pulse</h1>
            {unreadCount > 0 ? (
              <span className="act-toolbar-unread tabular-nums">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            ) : null}
          </div>
        </div>
        <div className="act-toolbar-center app-chrome-clock-center">
          <AppLiveClock className="app-chrome-clock" />
        </div>
        <div className="act-toolbar-actions">
          <button
            type="button"
            className="act-btn-ghost act-btn-icon"
            onClick={refresh}
            disabled={refreshing}
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
          </button>
          {unreadCount > 0 ? (
            <button
              type="button"
              className="act-btn-primary act-btn-icon"
              onClick={handleMarkAll}
              disabled={markAllLoading}
              aria-busy={markAllLoading}
              aria-label="Mark all seen"
              title="Mark all seen"
            >
              {markAllLoading ? (
                <Loader2 size={15} className="animate-spin" aria-hidden />
              ) : (
                <CheckCheck size={15} aria-hidden />
              )}
            </button>
          ) : null}
        </div>
      </header>

      <div className="act-page-body">
        {error && (
          <div className="act-error">
            {String(error?.error || error?.message || error)}
          </div>
        )}

        <div className="act-pulse-band">
          <ActivityHero unreadCount={unreadCount} total={pagination.total} />
          <PreviewStrip preview={summaryPreview} />
        </div>

        <div className="act-filters" role="tablist" aria-label="Filter activity">
          {ACTIVITY_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filter === f.id}
              className={`act-filter-chip ${filter === f.id ? "act-filter-chip-active" : ""}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
              {f.id === "unread" && unreadCount > 0 ? (
                <span className="act-filter-badge">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              ) : null}
            </button>
          ))}
        </div>

        <section className="act-timeline" aria-label="Activity timeline">
        <div className="act-timeline-head">
          <p className="act-timeline-label">Timeline</p>
        </div>

        {loading && !filtered.length && (
          <div className="act-loading">
            <Loader2 size={20} className="animate-spin" />
            <span>Loading your feed</span>
          </div>
        )}

        {!loading && !filtered.length && (
          <div className="act-empty">
            <ActivityIcon size={32} style={{ color: "rgba(30,136,229,0.45)" }} />
            <h2>Quiet channel</h2>
            <p>Place a bet, move cash, or get invited — your story builds here.</p>
          </div>
        )}

        {displaySections.map((section) => (
          <div key={section.dateKey} className="act-day-group">
            <div className="act-day-head">
              <span className="act-day-pill">{section.title}</span>
              <span className="act-day-line" aria-hidden />
            </div>

            <div className="act-day-rows">
              {section.visible.map((item) =>
                isFollowingBetHouseLiveActivity(item) ? (
                  <FollowingBetHouseLiveActivityCard
                    key={item._id}
                    item={item}
                    unread={!item.isRead}
                    onMarkRead={markActivitySeen}
                    onViewHouse={handleViewOffer}
                    onDelete={handleDelete}
                  />
                ) : isFollowingPredictionActivity(item) ? (
                  <FollowingPredictionActivityCard
                    key={item._id}
                    item={item}
                    unread={!item.isRead}
                    onMarkRead={markActivitySeen}
                    onViewPrediction={handleViewOffer}
                    onDelete={handleDelete}
                  />
                ) : isFollowingP2PActivity(item) ? (
                  <FollowingP2PActivityCard
                    key={item._id}
                    item={item}
                    unread={!item.isRead}
                    onMarkRead={markActivitySeen}
                    onViewOffer={handleViewOffer}
                    onDelete={handleDelete}
                  />
                ) : (
                  <ActivityRow
                    key={item._id}
                    item={item}
                    onRead={markActivitySeen}
                    onDelete={handleDelete}
                  />
                )
              )}
            </div>

            {(section.showNewer || section.showOlder) && (
              <div className="act-day-controls">
                {section.rangeLabel && (
                  <span className="act-day-range">{section.rangeLabel}</span>
                )}
                <div className="act-day-btns">
                  {section.showNewer && (
                    <button
                      type="button"
                      className="act-day-btn"
                      onClick={() =>
                        setSectionWindows((prev) => ({
                          ...prev,
                          [section.dateKey]: Math.max(0, (prev[section.dateKey] ?? 0) - 1),
                        }))
                      }
                    >
                      See previous
                    </button>
                  )}
                  {section.showOlder && (
                    <button
                      type="button"
                      className="act-day-btn"
                      onClick={() =>
                        setSectionWindows((prev) => ({
                          ...prev,
                          [section.dateKey]: (prev[section.dateKey] ?? 0) + 1,
                        }))
                      }
                    >
                      See more
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        <div ref={loadMoreRef} className="act-load-more">
          {loading && filtered.length > 0 && (
            <>
              <Loader2 size={16} className="animate-spin" />
              <span>Loading more</span>
            </>
          )}
        </div>

        <p className="act-hint">Click an unread line to mark it seen · hover to remove</p>
        </section>
      </div>
    </div>
  );
};

export default Activity;
